import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import { InventoryStatCard, InventorySearch, InventoryTable } from './components';
import {
  InventoryItem,
  InventoryStats,
  VelocityType,
  ExpiryStatus,
} from './inventoryTypes';
import { useSkusStore } from '../../../store/skusStore';
import { Sku } from '../../../services/skusApi';
import { getInventorySummary, InventorySummaryRow } from '../../../services/inventoryApi';
import styles from './AdminInventory.module.css';

// Velocity has no historical-sales source yet; this is an on-hand heuristic, not a real rate.
function deriveVelocity(stock: number): VelocityType {
  if (stock > 1000) return 'Fast';
  if (stock > 200) return 'Medium';
  return 'Slow';
}

function expiryStatusFor(days: number | null): ExpiryStatus {
  if (days === null) return 'normal';
  if (days <= 7) return 'exp-soon';
  if (days <= 30) return 'expiring';
  return 'normal';
}

function adapt(s: Sku, summary?: InventorySummaryRow): InventoryItem {
  const available = summary ? summary.available : s.total_stock;
  const onHand = summary ? summary.on_hand : s.total_stock;
  const daysToExpiry = summary?.earliest_expiration
    ? Math.round((new Date(summary.earliest_expiration).getTime() - Date.now()) / 86400000)
    : null;
  // Days until reorder: how much buffer above the reorder point remains (real when reorder_point set).
  const daysUntilReorder = s.reorder_point > 0 ? Math.max(0, available - s.reorder_point) : 0;
  return {
    id: String(s.id),
    productName: s.product_name,
    sku: s.sku_code,
    barcode: s.barcode,
    category: s.category_name || '—',
    velocity: deriveVelocity(available),
    unitsInHand: onHand,
    daysUntilReorder,
    turnover: available > 0 ? available / 100 : 0,
    stockPerUnit: available,
    expiry: summary?.earliest_expiration ? new Date(summary.earliest_expiration).toLocaleDateString() : '—',
    expiryStatus: expiryStatusFor(daysToExpiry),
    profitPerUnit: s.list_price && s.unit_cost ? s.list_price - s.unit_cost : 0,
  };
}

const AdminInventory: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');

  const items = useSkusStore((s) => s.items);
  const loading = useSkusStore((s) => s.loading);
  const error = useSkusStore((s) => s.error);
  const fetchList = useSkusStore((s) => s.fetchList);
  const [summaryBySku, setSummaryBySku] = useState<Map<number, InventorySummaryRow>>(new Map());

  useEffect(() => {
    fetchList();
    getInventorySummary()
      .then((rows) => setSummaryBySku(new Map(rows.map((r) => [r.sku_id, r]))))
      .catch(() => setSummaryBySku(new Map()));
  }, [fetchList]);

  const adapted = useMemo<InventoryItem[]>(
    () => items.map((s) => adapt(s, summaryBySku.get(s.id))),
    [items, summaryBySku]
  );

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return adapted;
    const q = searchQuery.toLowerCase();
    return adapted.filter(
      (it) =>
        it.productName.toLowerCase().includes(q) ||
        it.sku.toLowerCase().includes(q) ||
        (it.barcode || '').toLowerCase().includes(q) ||
        it.category.toLowerCase().includes(q)
    );
  }, [adapted, searchQuery]);

  const totalValue = items.reduce((sum, s) => sum + s.total_stock * s.unit_cost, 0);
  // Reorder-aware: below the SKU's reorder point, or the 50-unit fallback when unset.
  const lowStockCount = items.filter((s) =>
    s.reorder_point > 0 ? s.total_stock <= s.reorder_point : s.total_stock < 50
  ).length;
  const avgMargin = items.length > 0
    ? items.reduce((sum, s) => sum + s.margin_percent, 0) / items.length
    : 0;

  const stats: InventoryStats = {
    totalSkus: String(items.length),
    inventoryValue: `$${Math.round(totalValue).toLocaleString()}`,
    lowStockItems: lowStockCount,
    avgMargin: `${avgMargin.toFixed(1)}%`,
  };

  return (
    <div className={styles.container}>
      <div className={styles.statsGrid}>
        <InventoryStatCard title="Total SKUs" value={stats.totalSkus} icon="skus" color="blue" />
        <InventoryStatCard title="Inventory Value" value={stats.inventoryValue} icon="value" color="green" />
        <InventoryStatCard title="Low Stock Items" value={String(stats.lowStockItems)} icon="lowStock" color="orange" />
        <InventoryStatCard title="Avg Margin" value={stats.avgMargin} icon="margin" color="teal" />
      </div>

      <InventorySearch value={searchQuery} onChange={setSearchQuery} />

      <AsyncBoundary
        loading={loading && items.length === 0}
        error={error}
        empty={!loading && filtered.length === 0}
        emptyMessage="No inventory items match the current filters"
        onRetry={() => fetchList()}
      >
        <InventoryTable items={filtered} />
      </AsyncBoundary>
    </div>
  );
};

export const AdminInventoryPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin', 'Warehouse Manager']}>
    <AdminInventory />
  </ProtectedRoute>
);

export { AdminInventory };
export default AdminInventory;
